import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

import { AddSceneTool } from 'js/src/agent/tools/AddSceneTool';
import { AddVoiceoverSceneTool } from 'js/src/agent/tools/AddVoiceoverSceneTool';
import { DeleteSceneTool } from 'js/src/agent/tools/DeleteSceneTool';
import { MoveSceneGroupTool } from 'js/src/agent/tools/MoveSceneGroupTool';
import { SetSceneRangeTool } from 'js/src/agent/tools/SetSceneRangeTool';
import { SplitSceneGroupTool } from 'js/src/agent/tools/SplitSceneGroupTool';

export const voiceoverVideoBasicsSkill: Skill = {
    name: SKILL_NAMES.voiceoverVideoBasics,
    description:
        'Shared multi-tool procedures for voiceover/talking-head scene groups — overlays, restructuring, reordering, re-timing, and coverage repair. Load whenever building a voiceover video (before working with the narration), and whenever editing one.',
    kind: 'howto',
    surfaces: ['sketch'],
    /**
     * A getter, not a plain string: the tool classes it interpolates transitively import the
     * skills registry, so this module and those tools form an import cycle. Deferring the
     * interpolation to first access — when LoadSkillTool serves the skill — sidesteps reading a
     * still-initializing tool class at module-eval time. See changeTemplate.ts for the full note.
     */
    get instructions(): string {
        return `# Voiceover Video Basics

Shared procedures for voiceover and talking-head scene groups — when to reach for
which tool.

## Captions and bottom-of-screen text
Captions render along the bottom of the screen. When captions are enabled for a
scene group, pick designs whose text sits away from the bottom — a bottom text
callout ends up colliding with the captions — and keep any text you position
manually clear of that band.

## Overlay vs structural break vs reorder
Target a scene group by its \`scene_group_id\` (shown in each \`@@ Scene Group <id> @@\`
header), never by a positional number.
- **\`${AddVoiceoverSceneTool.toolName}\`** — a visual overlay while the speech keeps flowing.
- **\`${SplitSceneGroupTool.toolName}\`** — an actual structural break: a section that needs to be
  independently editable, a silent gap, or content you'll reorder.
- **\`${MoveSceneGroupTool.toolName}\`** — reorder after splitting. Position the group with
  \`after_scene_group_id\` (it lands immediately after that group; omit to move it to the
  very start).
Don't fake a break by chaining overlays, and don't split a group just to add one
overlay.

## Section headings that break up a voiceover
Requests like "section titles between the parts" or "break the video into segments"
call for silent, no-voiceover cards *between* the spoken parts. Structural fact that
governs how: a voiceover scene group plays as one continuous run — every
\`${AddVoiceoverSceneTool.toolName}\` overlay lives INSIDE it. A silent card (a regular scene added
with \`${AddSceneTool.toolName}\`) is a SEPARATE group, so it can only sit before or after a voiceover
group, never between that group's overlays. You therefore cannot segment narration by
dropping cards between overlays; the overlays are one contiguous group and every card
collapses to the start or end of it.

To place a silent card between two spoken parts, split the narration there first:
1. \`${SplitSceneGroupTool.toolName}\` at each section boundary — K spoken sections need K-1 splits,
   turning the one voiceover group into one group per section.
2. Add each card with \`${AddSceneTool.toolName}\`, \`after_scene_group_id\` = the voiceover group it
   follows, so it lands in the seam between two spoken groups (omit the anchor to put a
   card at the very start).

The number of cards you can place mid-narration equals the number of splits you made:
split zero times and every card piles up at the start or end of the single block. Build
section by section — split off a section, drop its card, repeat — rather than adding all
the cards up front and trying to reposition them.

<example goal="a title, then 3 narrated sections each led by a silent card">
  Full narration starts as one group. Split it at the 2 internal section boundaries →
  3 voiceover groups (S1, S2, S3). Add the title card at the start; add card "1" after
  the title (before S1), card "2" after S1, card "3" after S2. Result:
  title · card1 · S1 · card2 · S2 · card3 · S3.
</example>

## Coverage repair
Every transcript word in a **voiceover** scene group must be covered by an overlay —
uncovered ranges render as raw waveform. Deleting an overlay (\`${DeleteSceneTool.toolName}\`) leaves its
span uncovered rather than reflowing its neighbours, so the gap is yours to fill: extend an
adjacent overlay onto those words with \`${SetSceneRangeTool.toolName}\`, or add a new word-bounded overlay
with \`${AddVoiceoverSceneTool.toolName}\`. When the \`overlay_coverage\` lint names uncovered words, cover
exactly those words. **Never** use \`cover_entire_video\` to fill a gap.

## Re-timing an overlay
\`${SetSceneRangeTool.toolName}(scene_id, words)\` re-times an existing overlay so it covers exactly the
words you pass — grow, shrink, or shift it while keeping its text/media. Neighbouring
overlays reflow to stay contiguous. The new range must overlap the overlay's current
coverage; to move an overlay somewhere unrelated, \`${DeleteSceneTool.toolName}\` and \`${AddVoiceoverSceneTool.toolName}\`
there instead.

## Targeting overlays
Every \`${AddVoiceoverSceneTool.toolName}\` result reports the \`scene_id\` it created — record and use
those scene_ids when setting the overlay's text and media. Never infer a scene's position
by counting; that drifts and lands content one scene off from its narration.`;
    },
};
